/**
 * Category Section Component
 * Expandable section for displaying extracted category and items
 */

import { ChevronDown, ChevronRight, Edit2 } from 'lucide-react';
import React from 'react';
import { ExtractedCategory, ExtractedItem } from '../../../../types/menu';
import { ItemEditor } from './ItemEditor';

interface CategorySectionProps {
  category: ExtractedCategory;
  categoryIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
  editingItem: { categoryIndex: number; itemIndex: number } | null;
  onEditItem: (itemIndex: number) => void;
  onSaveItem: (itemIndex: number, item: ExtractedItem) => void;
  onCancelEdit: () => void;
}

export const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  categoryIndex,
  isExpanded,
  onToggle,
  editingItem,
  onEditItem,
  onSaveItem,
  onCancelEdit,
}) => {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown size={16} className="text-gray-400" />
          ) : (
            <ChevronRight size={16} className="text-gray-400" />
          )}
          <span className="font-medium text-gray-900">
            {category.category_name}
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {category.items.length} items
        </span>
      </button>

      {isExpanded && (
        <div className="bg-gray-50 px-4 py-2 space-y-2">
          {category.items.map((item, itemIndex) => {
            const isEditing =
              editingItem?.categoryIndex === categoryIndex &&
              editingItem?.itemIndex === itemIndex;

            if (isEditing) {
              return (
                <ItemEditor
                  key={item.item_name}
                  item={item}
                  onSave={(updatedItem) => onSaveItem(itemIndex, updatedItem)}
                  onCancel={onCancelEdit}
                />
              );
            }

            return (
              <div
                key={item.item_name}
                className="bg-white rounded-lg px-3 py-2 border border-gray-100 flex items-center justify-between group"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.item_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.department} • ${item.base_price.toFixed(2)}
                    {item.addons.length > 0 &&
                      ` • ${item.addons.length} addons`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onEditItem(itemIndex)}
                  className="p-1 text-gray-400 hover:text-pos-primary opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Edit2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
